// Shapes returned by /api/admin/telemetry/* (backend admin_telemetry.py).
export type Surface = "widget" | "mobile";
export type KeyCount = { key: string; count: number };

export type ScreenTimeRow = { surface: Surface; screen: string; views: number; median_ms: number; total_ms: number };

export type ErrorStats = { total: number; by_class: KeyCount[]; by_source: KeyCount[]; by_day: { date: string; count: number }[] };

export type UploadStats = {
  queued: number;
  failed: number;
  permanent: number;
  recovered: number;
  by_class: KeyCount[];
  server_failed_recordings?: number;
};

export type ProblemUser = { user_id: string; email: string; errors: number; failed_uploads: number; last_seen: string | null };

export type TelemetryOverview = {
  days: number;
  active_users: Record<Surface, number>;
  sessions: number;
  screen_time: ScreenTimeRow[];
  errors: ErrorStats;
  uploads: UploadStats;
  problem_users: ProblemUser[];
};

export type TelemetryUserDetail = {
  days: number;
  user_id: string;
  sessions: { session_id: string; surface: Surface; version: string | null; started_at: string; length_ms: number }[];
  screen_time: ScreenTimeRow[];
  errors: ErrorStats;
  uploads: UploadStats;
  recent_errors: { created_at: string; surface: Surface; screen: string | null; error_class: string | null; source: string | null; detail: string | null }[];
  recent_uploads: { created_at: string; surface: Surface; kind: string; error_class: string | null; attempt: number | null; metadata: Record<string, unknown> }[];
};

export const SURFACE_LABEL: Record<Surface, string> = { widget: "Widget", mobile: "Mobiel" };
export const CLASS_LABEL: Record<string, string> = {
  network: "Netwerk", auth: "Authenticatie", server: "Server", client: "Client", permission: "Toestemming",
  audio: "Audio", pipeline: "Pipeline", unhandled: "Onafgevangen", other: "Overig",
};
export const KIND_LABEL: Record<string, string> = {
  upload_queued: "In wachtrij", upload_failed: "Mislukt", upload_recovered: "Hersteld",
};

// ── Problemen drill-down (explorer) ─────────────────────────────────────
export type ExplorerKind = "error" | "upload_failed" | "upload_queued" | "upload_recovered";

// One filter set drives both the list (/events) and the grouped (/messages) view.
// `detail: ""` means "rows without a message" (mirrors the backend contract).
export type ExplorerFilters = {
  kind: ExplorerKind;
  error_class?: string;
  source?: string;
  screen?: string;
  surface?: Surface;
  date?: string;
  user?: { id: string; email: string };
  detail?: string;
};

export type TelemetryEvent = {
  id: number;
  user_id: string;
  email: string;
  organization: string | null;
  organization_id: string | null;
  surface: Surface;
  version: string | null;
  platform: string | null;
  hostname: string | null;
  session_id: string;
  kind: string;
  screen: string | null;
  duration_ms: number | null;
  error_class: string | null;
  source: string | null;
  detail: string | null;
  attempt: number | null;
  metadata: Record<string, unknown>;
  client_ts: string;
  created_at: string;
};

export type TelemetryEventsPage = { events: TelemetryEvent[]; next_before_id: number | null };

export type TelemetryMessage = {
  error_class: string;
  source: string;
  detail: string | null;
  count: number;
  users: number;
  surfaces: Surface[];
  first_seen: string;
  last_seen: string;
};

export const EXPLORER_KIND_LABEL: Record<ExplorerKind, string> = {
  error: "Fouten", upload_failed: "Mislukte uploads", upload_queued: "Uploads in wachtrij", upload_recovered: "Herstelde uploads",
};
